import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  BarChartComponent,
  BarChartData,
  BarChartPatternType,
  InfoBoxComponent,
  SpinnerComponent,
  EmptyDataPlaceholderComponent,
} from '@shared/components';
import { DateHelper, cssVar } from '@shared/helpers';
import { FaultAnalysisHistoryItem, ResolutionCount } from '@shared/models';
import { FaultsStore } from '@shared/state';
import { BRCBaseComponent } from '@brc/hrt-base-component';

interface SeriesDefinition {
  id: string;
  dataKey: string;
  translationKey: string;
  translatedName: string;
  stack: string;
  color: string;
  pattern?: BarChartPatternType;
  orderInLegend: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-fault-history',
  imports: [TranslatePipe, BarChartComponent, InfoBoxComponent, SpinnerComponent, EmptyDataPlaceholderComponent],
  template: `
    <gea-hrt-info-box boxTitle="FAULT_ANALYSIS.HISTORY.TITLE" tooltip="FAULT_ANALYSIS.HISTORY.TOOLTIP" [withBorder]="false">
      <div class="history-container">
        @if (loading()) {
          <div class="center">
            <gea-hrt-spinner [diameter]="40" />
          </div>
        } @else {
          @let _chartData = chartData();
          @if (_chartData) {
            <gea-hrt-bar-chart [data]="_chartData" width="100%" height="400px" />
          } @else {
            <div class="center no-data">
              @let days = dateRange();

              <gea-hrt-empty-data-placeholder
                [useRawText]="true"
                [text]="
                  'FAULT_ANALYSIS.HISTORY.NO_DATA_AVAILABLE'
                    | translate
                      : {
                          dateCount: days === 1 ? 24 : (days ?? 0) / 7,
                          dateUnit: days === 1 ? ('UI-LIB.GENERAL.HOUR' | translate) : ('UI-LIB.GENERAL.WEEK' | translate),
                        }
                " />
            </div>
          }
        }
      </div>
    </gea-hrt-info-box>
  `,
  styles: `
    :host {
      margin-top: 2rem;
    }
    .history-container {
      position: relative;
      width: 100%;
      height: 400px; /* match the chart's height to avoid layout jump */
    }

    .center {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 50%;
      text-align: center;
    }
  `,
})
export class FaultHistoryComponent extends BRCBaseComponent implements OnInit {
  readonly chartData = signal<BarChartData<string, number> | undefined>(undefined);
  readonly loading = signal(true);
  private readonly faultHistory = signal<FaultAnalysisHistoryItem[]>([]);
  private readonly faultsStore = inject(FaultsStore);

  readonly dateRange = this.faultsStore.selectedDateRange;

  seriesToShow: SeriesDefinition[] = [
    {
      id: 'shutdowns.solved',
      dataKey: 'shutdowns.solved',
      translationKey: 'FAULT_ANALYSIS.HISTORY.SOLVED_SHUTDOWN',
      translatedName: '',
      stack: 'shutdown',
      color: 'green',
      pattern: 'diagonal-line',
      orderInLegend: 4,
    },
    {
      id: 'shutdowns.pending',
      dataKey: 'shutdowns.pending',
      translationKey: 'FAULT_ANALYSIS.HISTORY.PENDING_SHUTDOWN',
      translatedName: '',
      stack: 'shutdown',
      color: 'red',
      orderInLegend: 2,
    },
    {
      id: 'warnings.solved',
      dataKey: 'warnings.solved',
      translationKey: 'FAULT_ANALYSIS.HISTORY.SOLVED_WARNING',
      translatedName: '',
      stack: 'warning',
      color: cssVar('--geaui-brighter-yellow'),
      pattern: 'diagonal-line',
      orderInLegend: 3,
    },
    {
      id: 'warnings.pending',
      dataKey: 'warnings.pending',
      translationKey: 'FAULT_ANALYSIS.HISTORY.PENDING_WARNING',
      translatedName: '',
      stack: 'warning',
      color: 'orange',
      orderInLegend: 1,
    },
  ];

  readonly translateService = inject(TranslateService);

  constructor() {
    super();

    effect(() => {
      const history = this.faultsStore.faultHistory();

      if (!history) {
        return;
      }

      untracked(() => {
        this.faultHistory.set(history);
        this.refreshChartData();
        this.loading.set(false);
      });
    });
  }

  ngOnInit(): void {
    this.translateLabels();
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.translateLabels();
      this.refreshChartData();
    });
  }

  private refreshChartData(): void {
    const data = this.faultHistory();
    this.chartData.set(data.length ? this.generateChartData(data) : undefined);
  }

  generateChartData(historyData: FaultAnalysisHistoryItem[]): BarChartData<string, number> {
    const locale = this.translateService.getCurrentLang();
    const sorted = [...historyData].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return {
      showTooltip: true,
      showLegend: true,
      legendPosition: ['bottom', 'right'],
      padding: { left: 0, right: 0 },
      xValues: sorted.map(({ timestamp }) => DateHelper.localize(new Date(timestamp), locale)),
      yValues: this.seriesToShow.map((bar) => {
        const [faultType, resolution] = bar.dataKey.split('.');
        return {
          name: bar.translatedName,
          color: bar.color,
          pattern: bar.pattern ? { type: bar.pattern, color: 'white' } : undefined,
          stackName: bar.stack,
          data: sorted.map((item) => {
            if (faultType && resolution) {
              const resolutionCount = item[faultType as keyof FaultAnalysisHistoryItem] as ResolutionCount;
              return resolutionCount[resolution as keyof ResolutionCount];
            }
            return 0;
          }),
        };
      }),
      legend: {
        data: [...this.seriesToShow]
          .sort((a, b) => a.orderInLegend - b.orderInLegend)
          .map(({ translatedName: name }) => ({ name })),
      },
    };
  }

  translateLabels(): void {
    this.seriesToShow = this.seriesToShow.map((row) => ({
      ...row,
      translatedName: this.translateService.instant(row.translationKey) as string,
    }));
  }
}
